// Pin-strip bookkeeping (#11846, item 17). The strip's order, groups and pin
// state are the server's (Data/strip.ts), so a view save can no longer drop
// pin state and a reorder can no longer pin anything. What stays here is the
// one rule the page applies itself: the strip always shows the open view, so
// unpinning the view you are ON would leave it where it is; the page opens
// the nearest remaining pin in its context instead.

import type { RmsView } from "../Data/views";

export type PinContextId = number | "general";

export const contextOf = (view: RmsView): PinContextId =>
  view.group_id ?? "general";

/**
 * The view to open when `unpinnedId` is unpinned while open: the pinned
 * neighbour before it in the same context, else the one after, else null
 * (nothing else is pinned there, so the open view stays where it is).
 */
export const viewToOpenAfterUnpin = (
  views: readonly RmsView[],
  unpinnedId: number
): RmsView | null => {
  const unpinned = views.find((v) => v.id === unpinnedId);
  if (!unpinned) return null;
  const context = contextOf(unpinned);
  const pins = views.filter(
    (v) => contextOf(v) === context && (!v.user_hidden || v.id === unpinnedId)
  );
  const index = pins.findIndex((v) => v.id === unpinnedId);
  if (index < 0) return null;
  const before = pins.slice(0, index).reverse()[0];
  const after = pins[index + 1];
  return before ?? after ?? null;
};

/**
 * A saved view written back into the list cache. A save response carries no
 * authorization columns (#12050): it reports `is_org_wide` false, permission
 * "view" and no `visibility`. What the list said still holds; a save changes
 * none of it.
 */
export const mergeSavedView = (cached: RmsView, saved: RmsView): RmsView => ({
  ...cached,
  ...saved,
  is_org_wide: cached.is_org_wide,
  current_user_permission: cached.current_user_permission,
  visibility: saved.visibility ?? cached.visibility,
});
